import { always, compose, curry, ifElse, includes, isEmpty, join, lensIndex, length, lt, map, match, not, over, pipe, replace, slice, split, take, test, toUpper, when } from '~/utils/lib/core/prelude'

/// ==========================================
///   Entities/_methods
/// ==========================================

/**
 * @desc
 *    Limit string according to provided length
 *    Same as `wIgControleLongueur($3: False)`
 */
export const stringLimit = curry((
  /** @type {number} */ size,
  /** @type {string} */ str
  ) => when(
    // @ts-expect-error
    compose(lt(size), length),
    slice(0, size),
    str
  )
)

/**
 * @desc
 *    Limit string according to provided length **without any space*
 *    Same as `wIgControleLongueur($3: True)`
 */
export const stringLimitWithoutSpace = curry((
  /** @type {number} */ size,
  /** @type {string} */ str
  ) => pipe(
    replace(/ /g, ''),
    stringLimit(size)
  )(str)
)

/**
 * @desc Keep characters matching the RegExp
 * @type {import('ts-toolbelt/out/Function/Curry').Curry<(regexp: RegExp, str: string) => string>}
 */
export const patternFilter = curry((
  regexp,
  str
  ) => ifElse(
    test(regexp),
    pipe(
      match(regexp),
      join('')
    ),
    always(''),
  )(str)
)

/**
 * @desc Remove characters matching the RegExp
 * @type {import('ts-toolbelt/out/Function/Curry').Curry<(regexp: RegExp, str: string) => string>}
 */
export const patternOmit = curry((regexp, str) => replace(regexp, '', str))

/**
 * @desc Remove characters accent or diacritic
 * @link https://stackoverflow.com/a/37511463
 */
export const takeOutAccentDiacritic = (/** @type {string} */ str ) => str
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')

/// ==========================================
///   Entities/sCh
/// ==========================================

/**
 * @desc
 *    sCh_TMajusculeInitiales `$tscarsep`
 *
 * @constant {string[]}
 */
export const INITIAL_LETTER_SEPERATION_CHARS = [
  ' ',
  '-',
  '\'',
]

/**
 * @function initialLetterToUpper
 * @desc
 *   sCh_TMajusculeInitiales
 *   Passe en majuscule les initiales de la chaîne  $1.
 *   Initiales : 1° lettre et lettres suivant les caractères " ","-" ou "'".
 */
export const initialLetterToUpper = (/** @type {string} */ str) => pipe(
  split(''),
  // 1° lettre en majuscule
  over(
    lensIndex(0),
    when(
      compose(not, isEmpty),
      compose(toUpper, takeOutAccentDiacritic)
    )
  ),
  // majuscules si caractère de séparations repéré en amont
  map.iterable(
    (char, index) => {
      if (index === 0)
        return char

      const previousChar = str[index - 1]
      const needUppercase = includes(
        previousChar,
        INITIAL_LETTER_SEPERATION_CHARS
      )

      if (not(needUppercase))
        return char

      return compose(toUpper, takeOutAccentDiacritic)(char)
    }
  ),
  join('')
)(str)
